import React from 'react';
import { Text } from '../text';
import { sliderTheme } from '../../styles/themes/slider';

export type MultiRangeValues = {
    minValue: number;
    maxValue: number;
};

export type MultiRangeSliderProps = {
    id?: string;
    initialValue?: MultiRangeValues;
    min?: number;
    max?: number;
    step?: number;
    disabled?: boolean;
    showLabel?: boolean;
    labels?: string[];
    ariaLabel?: string;
    onChange?: (e: MultiRangeValues) => void;
    onDrop?: (e: MultiRangeValues) => void;
};

const RANGE_OFFSET = 20;

export const MultiRangeSlider = React.forwardRef<HTMLDivElement, MultiRangeSliderProps>((props, ref) => {
    // Props
    const {
        id,
        initialValue,
        min = 0,
        max = 100,
        step = 1,
        disabled = false,
        showLabel = true,
        labels = [],
        ariaLabel,
        onChange,
        onDrop,
    } = props;

    // Local state
    const [minValue, setMinValue] = React.useState(initialValue?.minValue || min);
    const [maxValue, setMaxValue] = React.useState(initialValue?.maxValue || max);
    const [isDraggingLeftThumb, setIsDraggingLeftThumb] = React.useState(false);
    const [isDraggingRightThumb, setIsDraggingRightThumb] = React.useState(false);

    // Variables
    const sliderLabels = labels.length ? labels : [min.toString(), Math.round(max / 2).toString(), max.toString()];
    const leftThumb = Math.max(((minValue - min) / (max - min)) * 100, 2);
    const rightThumb = Math.min(((maxValue - min) / (max - min)) * 100, 100);

    const handleLeftThumbDown = () => setIsDraggingLeftThumb(true);

    const handleRightThumbDown = () => setIsDraggingRightThumb(true);

    const getMousePosition = (e: React.MouseEvent) => {
        const thumb = e?.target as HTMLDivElement;
        const bar = thumb?.parentNode as HTMLDivElement;
        const barBox = bar?.getBoundingClientRect();
        const fraction = (e?.clientX - barBox?.left) / barBox?.width;
        return Math.round(min + fraction * (max - min));
    };

    const handleMouseMove = (event: React.MouseEvent) => {
        if (isDraggingLeftThumb) {
            const newValue = Math.ceil(Math.max(getMousePosition(event), min) / step) * step;
            if (newValue >= min && newValue <= maxValue) {
                setMinValue(newValue);
                onChange?.({ minValue: newValue, maxValue });
            }
        } else if (isDraggingRightThumb) {
            const newValue = Math.ceil(Math.max(getMousePosition(event), min) / step) * step;
            if (newValue >= minValue && newValue <= max) {
                setMaxValue(newValue);
                onChange?.({ minValue, maxValue: newValue });
            }
        }
    };

    const handleMouseUp = () => {
        setIsDraggingLeftThumb(false);
        setIsDraggingRightThumb(false);
        onDrop?.({ minValue, maxValue });
    };

    return (
        <div
            ref={ref}
            id={id}
            data-testid="multi-range-slider"
            className={sliderTheme.base}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            aria-label={ariaLabel}
        >
            <div className="relative w-full">
                <div className="mx-1">
                    <input
                        id="range-left"
                        type="range"
                        step={step}
                        min={min}
                        max={max}
                        onMouseDown={handleLeftThumbDown}
                        value={minValue}
                        className={sliderTheme.input}
                        style={{ left: `calc(${leftThumb}% - ${RANGE_OFFSET}px)` }}
                        disabled={disabled}
                        readOnly
                    />
                    <input
                        id="range-right"
                        type="range"
                        step={step}
                        min={min}
                        max={max}
                        onMouseDown={handleRightThumbDown}
                        value={maxValue}
                        className={sliderTheme.input}
                        style={{ left: `calc(${rightThumb}% - ${RANGE_OFFSET}px)` }}
                        disabled={disabled}
                        readOnly
                    />
                    <div className={sliderTheme.range}>
                        <div className={sliderTheme.bar} />
                        <div
                            className={sliderTheme.innerBar}
                            style={{ left: `calc(${leftThumb}% - 2px)`, right: `${100 - rightThumb}%` }}
                        />
                        <div
                            className={`${sliderTheme.handle.base} ${sliderTheme.handle.left}`}
                            style={{ left: `calc(${leftThumb}% - ${RANGE_OFFSET}px)` }}
                        />
                        <div
                            className={`${sliderTheme.handle.base} ${sliderTheme.handle.right}`}
                            style={{ left: `calc(${rightThumb}% - ${RANGE_OFFSET}px)` }}
                        />
                    </div>
                </div>

                {showLabel && sliderLabels.length ? (
                    <div className={sliderTheme.label}>
                        {sliderLabels.map((label) => (
                            <Text key={label} variant="body-small" align="center">
                                {label}
                            </Text>
                        ))}
                    </div>
                ) : null}
            </div>
        </div>
    );
});

MultiRangeSlider.displayName = 'MultiRangeSlider';
